/**
 * Тред обсуждения (GitHub Issue label pflow:comment) — общий компонент
 * для правой панели (EntityCard.DiscussionTab) и чат-панели (CommentsTab).
 *
 * Внутри: тело + ответы (issue comments), инлайн-поле ответа, резолв/
 * переоткрытие. Ответ пишется прямо в конкретный тред — не через агента
 * (агент не знал, в какой issue отвечать, и плодил новые комментарии).
 *
 * `showTarget` — показать бейдж сущности `Тип."Имя"` (в чат-панели треды
 * со всего проекта, надо видеть, к чему они).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Anchor, Avatar, Badge, Box, Button, Collapse, Divider, Group, Loader,
    Stack, Text, Textarea, UnstyledButton,
} from '@mantine/core';
import { IconRobot } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    fetchCommentThread, replyToComment, setCommentState, type PflowComment,
} from '../lib/api-client';
import { linkifyMentionsInMarkdown, MentionAwareLink } from '../lib/entity-mentions';
import { useUiStore } from '../stores/ui-store';

export function CommentThread({ owner, repo, comment, isAuthed, showTarget = false, onChanged }: {
    owner: string; repo: string; comment: PflowComment;
    isAuthed: boolean; showTarget?: boolean; onChanged: () => void;
}): JSX.Element {
    const [expanded, setExpanded] = useState(false);
    const [reply, setReply]       = useState('');
    const queryClient             = useQueryClient();
    const openCard                = useUiStore(s => s.openCard);

    const threadQ = useQuery({
        queryKey: ['comment-thread', owner, repo, comment.id],
        queryFn:  () => fetchCommentThread({ owner, repo, id: comment.id }),
        enabled:  expanded && comment.replies > 0,
        retry:    false,
    });

    const replyM = useMutation({
        mutationFn: () => replyToComment({ owner, repo, id: comment.id, text: reply.trim() }),
        onSuccess:  () => {
            setReply('');
            void queryClient.invalidateQueries({ queryKey: ['comment-thread', owner, repo, comment.id] });
            onChanged();
        },
    });

    const stateM = useMutation({
        mutationFn: (state: 'open' | 'closed') => setCommentState({ owner, repo, id: comment.id, state }),
        onSuccess:  onChanged,
    });

    const requestChat = useUiStore(s => s.requestChat);
    const resolved = comment.state === 'closed';
    // Бейдж цели `Тип."Имя"` → открыть карточку сущности.
    const targetRef = ((): { type: string; name: string } | null => {
        const m = comment.target.match(/^([^.]+)\."(.+)"$/);
        return m ? { type: m[1], name: m[2] } : null;
    })();

    // «Обсудить с агентом»: открыть чат с контекстом этого треда — цель +
    // текст комментария (в кавычках), чтобы агент понимал, о чём речь.
    const discussWithAgent = (): void => {
        const where = comment.target ? ` к ${comment.target}` : '';
        const quote = comment.text.length > 400 ? comment.text.slice(0, 400) + '…' : comment.text;
        requestChat(`Помоги разобраться с обсуждением${where} (@${comment.author}):\n\n> ${quote.replace(/\n/g, '\n> ')}\n\nМой вопрос: `);
    };

    return (
        <Box style={{
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 6, opacity: resolved ? 0.65 : 1,
        }}>
            <Box p={8}>
                {showTarget && comment.target && (
                    <Badge size="xs" variant="light" color="blue" mb={6}
                        style={{ textTransform: 'none', cursor: targetRef ? 'pointer' : 'default', maxWidth: '100%' }}
                        onClick={() => targetRef && openCard(targetRef)}>
                        {comment.target}
                    </Badge>
                )}
                <Group gap={8} wrap="nowrap" align="flex-start">
                    <Avatar src={comment.authorAvatar} size="sm" radius="xl" />
                    <Box style={{ minWidth: 0, flex: 1 }}>
                        <Group gap={6} wrap="nowrap">
                            <Text fz="xs" fw={600}>{comment.author}</Text>
                            <Anchor href={comment.url} target="_blank" rel="noreferrer" fz="xs" c="dimmed">
                                {comment.createdAt.slice(0, 10)}
                            </Anchor>
                            {resolved && (
                                <Badge size="xs" variant="light" color="green" style={{ textTransform: 'none' }}>
                                    решено
                                </Badge>
                            )}
                        </Group>
                        <Box fz="xs" style={{ wordBreak: 'break-word' }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}
                                components={{ a: MentionAwareLink }}>
                                {linkifyMentionsInMarkdown(comment.text)}
                            </ReactMarkdown>
                        </Box>
                    </Box>
                </Group>

                <Group gap={10} mt={4} pl={32}>
                    <UnstyledButton onClick={() => setExpanded(e => !e)}>
                        <Text fz="xs" c="blue.6">
                            {expanded ? 'Свернуть' : comment.replies > 0 ? `Ответы (${comment.replies})` : 'Ответить'}
                        </Text>
                    </UnstyledButton>
                    <UnstyledButton onClick={discussWithAgent}>
                        <Group gap={3} wrap="nowrap">
                            <IconRobot size={12} color="var(--mantine-color-blue-6)" />
                            <Text fz="xs" c="blue.6">Обсудить с агентом</Text>
                        </Group>
                    </UnstyledButton>
                    {isAuthed && (
                        <UnstyledButton
                            onClick={() => stateM.mutate(resolved ? 'open' : 'closed')}
                            disabled={stateM.isPending}
                        >
                            <Text fz="xs" c="dimmed">{resolved ? 'Переоткрыть' : 'Решено ✓'}</Text>
                        </UnstyledButton>
                    )}
                </Group>
            </Box>

            <Collapse in={expanded}>
                <Divider />
                <Box p={8} pl={16}>
                    {threadQ.isLoading && <Loader size="xs" />}
                    <Stack gap={8}>
                        {(threadQ.data?.replies ?? []).map(r => (
                            <Group key={r.id} gap={8} wrap="nowrap" align="flex-start">
                                <Avatar src={r.authorAvatar} size={20} radius="xl" />
                                <Box style={{ minWidth: 0, flex: 1 }}>
                                    <Group gap={6} wrap="nowrap">
                                        <Text fz="xs" fw={600}>{r.author}</Text>
                                        <Text fz="xs" c="dimmed">{r.createdAt.slice(0, 10)}</Text>
                                    </Group>
                                    <Box fz="xs" style={{ wordBreak: 'break-word' }}>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}
                                            components={{ a: MentionAwareLink }}>
                                            {linkifyMentionsInMarkdown(r.text)}
                                        </ReactMarkdown>
                                    </Box>
                                </Box>
                            </Group>
                        ))}
                    </Stack>
                    {isAuthed && (
                        <Group gap={6} mt={8} wrap="nowrap" align="flex-end">
                            <Textarea flex={1} placeholder="Ответить…" autosize minRows={1} maxRows={4} size="xs"
                                value={reply} onChange={e => setReply(e.currentTarget.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && reply.trim()) {
                                        e.preventDefault();
                                        replyM.mutate();
                                    }
                                }} />
                            <Button size="xs" variant="light"
                                disabled={reply.trim().length === 0}
                                loading={replyM.isPending}
                                onClick={() => replyM.mutate()}>
                                Ответить
                            </Button>
                        </Group>
                    )}
                    {replyM.isError && <Text fz="xs" c="red.7" mt={4}>{(replyM.error as Error).message}</Text>}
                </Box>
            </Collapse>
        </Box>
    );
}

export type { PflowComment };
